import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Monitor } from 'lucide-react';
import type { UIDefinition, BindingContext } from '@fias/sdui-types';
import { PageRenderer } from '../../sdui';
import { JsonEditor } from '../components';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Default UI definition for demonstration
const defaultUIDefinition: UIDefinition = {
  version: '1.0',
  defaultPage: 'main',
  pages: {
    main: {
      id: 'main',
      title: 'Preview Page',
      layout: [
        {
          type: 'Container',
          id: 'container-1',
          props: { maxWidth: 'md', padding: 'lg' },
          children: [
            {
              type: 'Heading',
              id: 'heading-1',
              props: { level: 1, text: 'Hello SDUI!' }
            },
            {
              type: 'Text',
              id: 'text-1',
              props: { text: 'This is a live preview of your SDUI components.' }
            },
            {
              type: 'Card',
              id: 'card-1',
              props: { title: 'Sample Card', padding: 'md' },
              children: [
                {
                  type: 'TextInput',
                  id: 'input-1',
                  props: { label: 'Name', placeholder: 'Enter your name' }
                },
                {
                  type: 'Button',
                  id: 'button-1',
                  props: { label: 'Submit', variant: 'primary' }
                }
              ]
            }
          ]
        }
      ]
    }
  }
};

const defaultContext: BindingContext = {
  input: {},
  output: {},
  user: { id: 'dev-user', displayName: 'Developer' },
  arche: { id: 'preview', name: 'Preview Arche' },
  request: { id: 'preview-request', currentPage: 'main' }
};

export function SDUIPreview() {
  const [uiDefinition, setUIDefinition] = useState<UIDefinition>(defaultUIDefinition);
  const [context, setContext] = useState<BindingContext>(defaultContext);
  const [currentPage, setCurrentPage] = useState<string>('main');
  const [showContext, setShowContext] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleUIChange = (value: unknown) => {
    try {
      const ui = value as UIDefinition;
      setUIDefinition(ui);
      // Reset to default page if current page doesn't exist
      if (ui.pages && !ui.pages[currentPage]) {
        setCurrentPage(ui.defaultPage || Object.keys(ui.pages)[0] || 'main');
      }
      setParseError(null);
    } catch (e) {
      setParseError((e as Error).message);
    }
  };

  const currentPageDef = uiDefinition.pages?.[currentPage];
  const pageOptions = uiDefinition.pages ? Object.keys(uiDefinition.pages) : [];

  return (
    <div className="flex flex-col h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dev-console">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="h-5 w-5" />
              </Button>
            </Link>
            <h1 className="text-xl font-semibold">SDUI Preview</h1>
          </div>
          <div className="flex items-center gap-4">
            {/* Page selector */}
            {pageOptions.length > 1 && (
              <Select value={currentPage} onValueChange={setCurrentPage}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {pageOptions.map((pageId) => (
                    <SelectItem key={pageId} value={pageId}>
                      {pageId}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              variant={showContext ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowContext(!showContext)}
            >
              Context
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left panel - JSON Editor */}
        <div className="w-1/2 flex flex-col border-r border-border bg-card">
          <div className="flex-1 p-4 overflow-auto">
            <JsonEditor
              value={showContext ? context : uiDefinition}
              onChange={showContext ? (v) => setContext(v as BindingContext) : handleUIChange}
              label={showContext ? 'Binding Context' : 'UI Definition'}
              height="calc(100% - 40px)"
            />
          </div>
          {parseError && (
            <div className="p-4 border-t border-destructive/30 bg-destructive/10">
              <p className="text-sm text-destructive">{parseError}</p>
            </div>
          )}
        </div>

        {/* Right panel - Preview */}
        <div className="w-1/2 overflow-auto bg-muted/30">
          <div className="min-h-full p-4">
            <Card className="min-h-full">
              {currentPageDef ? (
                <PageRenderer page={currentPageDef} className="p-4" />
              ) : (
                <div className="flex items-center justify-center h-64 text-muted-foreground">
                  <div className="text-center">
                    <Monitor className="h-12 w-12 mx-auto mb-4" />
                    <p>No page to display</p>
                    <p className="text-sm mt-1">
                      Edit the UI definition to see a preview
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SDUIPreview;
